import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  type InputSignal,
  type OutputEmitterRef,
  type Signal,
} from '@angular/core';
import { UiInputType } from '../../types/ui-input-type.type';

@Component({
  selector: 'ui-input',
  imports: [],
  templateUrl: './ui-input.component.html',
  styleUrl: './ui-input.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UiInputComponent {
  private static nextId = 0;

  public readonly id: InputSignal<string | null> = input<string | null>(null);
  public readonly label: InputSignal<string | null> = input<string | null>(null);
  public readonly value: InputSignal<string> = input<string>('');
  public readonly placeholder: InputSignal<string> = input<string>('');
  public readonly type: InputSignal<UiInputType> = input<UiInputType>('text');
  public readonly name: InputSignal<string | null> = input<string | null>(null);
  public readonly autocomplete: InputSignal<string | null> = input<string | null>(null);
  public readonly inputMode: InputSignal<string | null> = input<string | null>(null);
  public readonly ariaLabel: InputSignal<string | null> = input<string | null>(null);
  public readonly helperText: InputSignal<string | null> = input<string | null>(null);
  public readonly errorText: InputSignal<string | null> = input<string | null>(null);
  public readonly maxLength: InputSignal<number | null> = input<number | null>(null);
  public readonly disabled: InputSignal<boolean> = input<boolean>(false);
  public readonly readonly: InputSignal<boolean> = input<boolean>(false);
  public readonly required: InputSignal<boolean> = input<boolean>(false);
  public readonly invalid: InputSignal<boolean> = input<boolean>(false);
  public readonly valueChange: OutputEmitterRef<string> = output<string>();
  public readonly blurred: OutputEmitterRef<void> = output<void>();

  protected readonly fallbackId: string = `ui-input-${(UiInputComponent.nextId += 1)}`;
  protected readonly inputId: Signal<string> = computed((): string => this.id() ?? this.fallbackId);
  protected readonly helperId: Signal<string> = computed((): string => `${this.inputId()}-helper`);
  protected readonly errorId: Signal<string> = computed((): string => `${this.inputId()}-error`);
  protected readonly hasLabel: Signal<boolean> = computed((): boolean => !!this.label());
  protected readonly hasHelper: Signal<boolean> = computed((): boolean => !!this.helperText());
  protected readonly hasError: Signal<boolean> = computed((): boolean => !!this.errorText());
  protected readonly isInvalid: Signal<boolean> = computed((): boolean => this.invalid() || this.hasError());
  protected readonly describedBy: Signal<string | null> = computed((): string | null => {
    if (this.hasError()) {
      return this.errorId();
    }

    if (this.hasHelper()) {
      return this.helperId();
    }

    return null;
  });

  protected handleInput(event: Event): void {
    const target: HTMLInputElement = event.target as HTMLInputElement;

    this.valueChange.emit(target.value);
  }

  protected handleBlur(): void {
    this.blurred.emit();
  }
}
